import { z } from 'zod';
/**
 * Game REST / Config DTO — Plan 08-02 Task 2 (GAME-01 · L4 compliance base)
 *                          + Plan 08-03 Task 1 (GAME-02/03/04/06 확장).
 *
 * Scope:
 *   - GameKind enum (3 종) — engine/interface 계약과 동형.
 *   - Prize 공통 schema (id, name, probability 0~1, imageUrl optional).
 *   - GameConfigBase — 활성화, 최소 후원 금액, 확률 공시 플래그, 19+ gate 요구 플래그.
 *   - 08-03: RouletteConfig / SlotConfig / RaceConfig + discriminated union
 *     + 현금 환원 refinement (GAME-06 사행성 차단).
 *
 * L4 compliance (DESIGN §21):
 *   - 확률 총합 refine 은 RouletteConfigSchema 에서 1.0 강제 (1e-3 허용).
 *   - `probabilityDisclosed` 는 literal(true) — false 저장 불가.
 *   - L4 게임(사행성 프레이밍 가능) 인 경우 `requiresAdultVerification=true` 필수.
 *   - cash_equivalent_krw 컬럼 존재 금지 (packages/db widget_games_configs 에서 차단)
 *     + config_json 문자열 regex scan (GameConfigNoCashRefinement) 2차 차단.
 */
export const GameKindSchema = z.enum(['roulette', 'slot', 'race']);
/**
 * 개별 상품 schema.
 * - probability: 0~1 범위 float. 08-03 각 game schema 가 총합=1 refine.
 * - value: game-specific (룰렛 상품명, 슬롯 jackpot ratio 등) — unknown.
 */
export const PrizeSchema = z
    .object({
    id: z.string().min(1).max(64),
    name: z.string().min(1).max(50),
    probability: z.number().min(0).max(1),
    imageUrl: z.string().url().optional(),
    value: z.unknown().optional(),
})
    .strict();
/**
 * 게임 공통 config — 08-03 각 game 이 `.extend(...)` 해 자기 필드 추가.
 */
export const GameConfigBaseSchema = z
    .object({
    /** 스트리머가 토글하는 ON/OFF. */
    enabled: z.boolean(),
    /** 참여 최소 후원 금액 (KRW 정수). 정책상 1,000원 이상 (Wave 0 결제 최소치). */
    minDonationKrw: z.number().int().min(1000),
    /** 확률 공시 노출 필수 — literal true. */
    probabilityDisclosed: z.literal(true),
    /** 19+ PASS gate 필요 여부 — L4 compliance. */
    requiresAdultVerification: z.boolean(),
})
    .strict();
// ---------------------------------------------------------------------------
// Plan 08-03 Task 1 — 3 game config schemas
// ---------------------------------------------------------------------------
/** 확률 배열 합산 — 부동소수 오차 1e-3 까지 허용 (refinement 헬퍼). */
export function sumProbabilities(arr) {
    if (arr.length === 0)
        return 0;
    let sum = 0;
    for (const v of arr) {
        sum += typeof v === 'number' ? v : v.probability;
    }
    return sum;
}
/**
 * GAME-06 사행성 차단 — config 내 임의 depth 에 '현금 환원' 관련 키/값 금지.
 * JSON.stringify 전체에 대한 regex scan (2차 방어; 1차는 DB 컬럼 부재).
 */
export const GameConfigNoCashRefinement = (data, ctx) => {
    const str = JSON.stringify(data);
    if (/cash[_\s-]?equivalent|payout[_\s-]?cash|convert[_\s-]?to[_\s-]?cash|환전|현금\s*환원/i.test(str)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: '현금 환원 관련 키/값 금지 (GAME-06 사행성 차단)',
            path: [],
        });
    }
};
/** Roulette config — 상품 배열 + 후원액 비례 회전수. 확률 합 1.0 강제. */
const RouletteConfigBase = GameConfigBaseSchema.extend({
    kind: z.literal('roulette'),
    prizes: z
        .array(PrizeSchema)
        .min(2)
        .refine((arr) => Math.abs(sumProbabilities(arr) - 1) < 1e-3, { message: '확률 합 ≠ 1.0 (1e-3 허용)' }),
    /** 후원 1,000원 당 회전수. 정수 ≥ 1. */
    rotationsPerKrw: z.number().int().min(1),
}).strict();
export const RouletteConfigSchema = RouletteConfigBase.superRefine(GameConfigNoCashRefinement);
/** Slot symbol — weighted. weight ≥ 0 (0 = 해당 릴에서 미출현). */
export const SlotSymbolSchema = z
    .object({
    id: z.string().min(1).max(64),
    label: z.string().min(1).max(32),
    weight: z.number().min(0),
})
    .strict();
/** Slot config — 3 릴 + 잭팟 확률 공시. jackpotProbability ∈ [0.001%, 1%]. */
const SlotConfigBase = GameConfigBaseSchema.extend({
    kind: z.literal('slot'),
    reels: z.literal(3),
    symbols: z.array(SlotSymbolSchema).min(3),
    jackpotSymbol: z.string().min(1),
    /** 잭팟 확률 공시 (0.001% ~ 1%). 공시 의무 — DESIGN §21.5. */
    jackpotProbability: z.number().min(0.00001).max(0.01),
}).strict();
export const SlotConfigSchema = SlotConfigBase.superRefine(GameConfigNoCashRefinement);
/** Race config — 4 말 + 메시지 parse regex + 베팅창(per-donation). */
const RaceConfigBase = GameConfigBaseSchema.extend({
    kind: z.literal('race'),
    horses: z.literal(4),
    /**
     * 후원 메시지에서 말 번호 parse regex. 캡처 그룹 1 = 말 번호(1~4).
     *
     * HI-02 방어: ReDoS 완화를 위해 (1) 최대 길이 200, (2) nested quantifier 패턴
     * (`(a+)+`, `(a*)*`, `(a+)*`, `(a*)+`) 거부. 런타임 race.game.ts 에서도 동일
     * 검증을 한 번 더 수행.
     */
    messageParseRegex: z
        .string()
        .max(200, 'messageParseRegex must be <=200 chars')
        .refine((v) => !/\([^)]*[+*][^)]*\)\s*[+*]/.test(v), 'messageParseRegex contains potentially catastrophic nested quantifier pattern')
        .default('(?:말|#말|horse[:\\s]?)\\s*([1-4])'),
    bettingWindow: z.literal('per_donation'),
}).strict();
export const RaceConfigSchema = RaceConfigBase.superRefine(GameConfigNoCashRefinement);
/**
 * Discriminated union — configJson 저장값은 3 schema 중 하나. discriminatedUnion
 * 은 ZodObject 만 멤버로 허용 (ZodEffects 불가) → base 버전 사용. 현금 환원
 * refinement 는 각 RouletteConfigSchema / SlotConfigSchema / RaceConfigSchema
 * (superRefine 래퍼) 에서 수행. 본 union 은 분기 판정 + shape 검증 목적.
 *
 * service layer (08-04) 는 kind 판정 후 refinement 포함된 schema 로 .parse 호출.
 */
export const GameConfigSchema = z.discriminatedUnion('kind', [
    RouletteConfigBase,
    SlotConfigBase,
    RaceConfigBase,
]);
// ---------------------------------------------------------------------------
// Plan 08-04 Task 1 — REST request/response schemas
// ---------------------------------------------------------------------------
/**
 * GET /api/v1/widget/games — 단일 스트리머의 전체 3-kind 상태.
 *
 * 각 kind 별로 저장된 row 가 없으면 default (`enabled=false, configJson={}`) 반환.
 * updatedAt 은 저장 이력이 없는 kind 일 경우 null.
 */
export const GetGameConfigsResponseSchema = z
    .object({
    games: z
        .object({
        roulette: z.object({
            enabled: z.boolean(),
            configJson: z.unknown(),
            requiresAdultVerification: z.boolean(),
            updatedAt: z.string().datetime().nullable(),
        }),
        slot: z.object({
            enabled: z.boolean(),
            configJson: z.unknown(),
            requiresAdultVerification: z.boolean(),
            updatedAt: z.string().datetime().nullable(),
        }),
        race: z.object({
            enabled: z.boolean(),
            configJson: z.unknown(),
            requiresAdultVerification: z.boolean(),
            updatedAt: z.string().datetime().nullable(),
        }),
    })
        .strict(),
})
    .strict();
/**
 * PUT /api/v1/widget/games/:kind — 단일 kind 활성화 + config 저장.
 *
 * `config` 는 서비스 계층이 kind 별 (`RouletteConfigSchema`/`SlotConfigSchema`/
 * `RaceConfigSchema`) 로 parse — 본 contract 는 shape 만 feed through.
 */
export const PutGameConfigRequestSchema = z
    .object({
    enabled: z.boolean(),
    config: z.unknown(),
})
    .strict();
export const PutGameConfigResponseSchema = z
    .object({
    ok: z.literal(true),
    kind: GameKindSchema,
    enabled: z.boolean(),
})
    .strict();
//# sourceMappingURL=game.rest.js.map